import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_ORIGIN = process.env.REACT_APP_SITE_URL || 'https://akirasignaturecare.com';
const BRAND_SUFFIX = 'Akira Signature Care';
const DEFAULT_OG = `${SITE_ORIGIN}/og-image.jpg`;

/**
 * Reusable SEO head for every route.
 * - title: short page label (e.g. "Services") — brand suffix is appended automatically.
 * - description: 130–160 char meta + OG description.
 * - path: starts with "/" (used for canonical + og:url).
 * - image: absolute URL or relative path (relative is auto-prefixed with origin).
 * - type: "website" | "article" | "profile"
 * - schema: optional Schema.org JSON-LD object (or array). Will be injected as <script type="application/ld+json">.
 * - noindex: true → adds noindex,nofollow.
 */
export default function SEO({
  title,
  description,
  path = '/',
  image,
  type = 'website',
  schema,
  noindex = false,
  lang,
  alternates,
}) {
  // If the caller-provided title already includes the brand (e.g. bespoke
  // SEO titles that already end with "| Akira" or "| Akira Signature Care"),
  // do not append the brand suffix again.
  const titleHasBrand = title && /\bakira\b/i.test(title);
  const fullTitle = title
    ? (titleHasBrand ? title : `${title} | ${BRAND_SUFFIX}`)
    : BRAND_SUFFIX;
  const url = `${SITE_ORIGIN}${path}`;
  const ogImage = !image
    ? DEFAULT_OG
    : image.startsWith('http')
    ? image
    : `${SITE_ORIGIN}${image}`;

  const schemas = Array.isArray(schema) ? schema : schema ? [schema] : [];
  const hreflangs = Array.isArray(alternates) ? alternates : [];

  return (
    <Helmet>
      {lang && <html lang={lang} />}
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* hreflang alternates */}
      {hreflangs.map((a) => (
        <link
          key={a.hreflang}
          rel="alternate"
          hrefLang={a.hreflang}
          href={a.href.startsWith('http') ? a.href : `${SITE_ORIGIN}${a.href}`}
        />
      ))}
      {hreflangs.length > 0 && (
        <link rel="alternate" hrefLang="x-default" href={url} />
      )}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:site_name" content="Akira Signature Care" />
      <meta property="og:locale" content={lang === 'es' ? 'es_ES' : 'en_US'} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:image" content={ogImage} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD per page */}
      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
}

export { SITE_ORIGIN, BRAND_SUFFIX };
